/**
 * Application Routes
 * Defines all routes for the admin application
 */

import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  AgentListPage,
  AgentConfigPage,
  LoginPage,
  MonitoringPage,
  SkillsPage,
  PushTestPage,
  SystemSettingsPage,
  EvaluationPage,
  PlaygroundPage,
  ImageWorkflowsPage,
  WorkflowSchedulesPage,
  ReportsPage,
  PromptTemplatesPage,
  UsersPage,
  AnnotationBatchesPage,
  AnnotationExportPage,
  AnnotationWorkbenchPage,
  AnnotationStatsPage,
  ClassificationAnalyticsPage,
  MessageFeedbackPage,
} from '@/pages';

// Check if user is authenticated
function isAuthenticated(): boolean {
  const token = localStorage.getItem('admin_token');
  return !!token;
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  if (!isAuthenticated()) {
    // Save the attempted path for redirect after login
    sessionStorage.setItem('admin_redirect_after_login', location.pathname);
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Login page - public */}
      <Route path="/login" element={<LoginPage />} />

      {/* Agent list - protected home page */}
      <Route path="/" element={
        <ProtectedRoute>
          <AgentListPage />
        </ProtectedRoute>
      } />

      {/* Agent configuration editor - protected */}
      <Route path="/agents/:agentType" element={
        <ProtectedRoute>
          <AgentConfigPage />
        </ProtectedRoute>
      } />

      {/* System Settings - protected */}
      <Route path="/system-settings" element={
        <ProtectedRoute>
          <SystemSettingsPage />
        </ProtectedRoute>
      } />

      {/* Skills Management - protected */}
      <Route path="/skills" element={
        <ProtectedRoute>
          <SkillsPage />
        </ProtectedRoute>
      } />

      {/* Service Monitoring - protected */}
      <Route path="/monitoring" element={
        <ProtectedRoute>
          <MonitoringPage />
        </ProtectedRoute>
      } />

      {/* Push Notification Test - protected */}
      <Route path="/push-test" element={
        <ProtectedRoute>
          <PushTestPage />
        </ProtectedRoute>
      } />

      {/* Evaluation Center - consolidated tab page */}
      <Route path="/evaluation" element={
        <ProtectedRoute>
          <EvaluationPage />
        </ProtectedRoute>
      } />
      <Route path="/evaluation/:tab" element={
        <ProtectedRoute>
          <EvaluationPage />
        </ProtectedRoute>
      } />

      {/* Legacy redirects for old evaluation URLs */}
      <Route path="/eval-scenarios" element={<Navigate to="/evaluation/scenarios" replace />} />
      <Route path="/eval-metrics" element={<Navigate to="/evaluation/metrics" replace />} />
      <Route path="/test-suites" element={<Navigate to="/evaluation/suites" replace />} />

      {/* Playground - protected */}
      <Route path="/playground" element={
        <ProtectedRoute>
          <PlaygroundPage />
        </ProtectedRoute>
      } />

      {/* Image Workflows - protected */}
      <Route path="/image-workflows" element={
        <ProtectedRoute>
          <ImageWorkflowsPage />
        </ProtectedRoute>
      } />

      {/* Workflow Schedules - protected */}
      <Route path="/workflow-schedules" element={
        <ProtectedRoute>
          <WorkflowSchedulesPage />
        </ProtectedRoute>
      } />

      {/* Reports Dashboard - protected */}
      <Route path="/reports" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/reports/:tab" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />

      {/* Prompt Templates - protected */}
      <Route path="/prompt-templates" element={
        <ProtectedRoute>
          <PromptTemplatesPage />
        </ProtectedRoute>
      } />

      {/* User Management - protected */}
      <Route path="/users" element={
        <ProtectedRoute>
          <UsersPage />
        </ProtectedRoute>
      } />

      {/* Classification Analytics - protected */}
      <Route path="/classification-analytics" element={
        <ProtectedRoute>
          <ClassificationAnalyticsPage />
        </ProtectedRoute>
      } />

      {/* Message Feedback - protected */}
      <Route path="/message-feedback" element={
        <ProtectedRoute>
          <MessageFeedbackPage />
        </ProtectedRoute>
      } />

      {/* Annotation System - protected */}
      <Route path="/annotation/batches" element={
        <ProtectedRoute>
          <AnnotationBatchesPage />
        </ProtectedRoute>
      } />
      <Route path="/annotation/export" element={
        <ProtectedRoute>
          <AnnotationExportPage />
        </ProtectedRoute>
      } />
      <Route path="/annotation/workbench" element={
        <ProtectedRoute>
          <AnnotationWorkbenchPage />
        </ProtectedRoute>
      } />
      <Route path="/annotation/stats" element={
        <ProtectedRoute>
          <AnnotationStatsPage />
        </ProtectedRoute>
      } />

      {/* Catch-all redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
